<template>
  <div id="app">
    <h1 v-html="name" />
    <p v-html="description" />
    <h2>Installation</h2>
    <pre><code>$ npm i vc-social-bar --save</code></pre>
    <h2>Usage</h2>
    <pre><code>// in main.js, use globally
import VcSocialBar from 'vc-social-bar'
import 'vc-social-bar/dist/lib/vc-social-bar.min.css'
Vue.use(VcSocialBar)

// as a component within another component
import VcSocialBar from 'vc-social-bar'
import 'vc-social-bar/dist/lib/vc-social-bar.min.css'

export default {
  name: 'app',
  components: {
    VcSocialBar
  }
}</code></pre>
    <h3>Adding to your App</h3>
    <pre><code>&lt;vc-social-bar
  title="My cool website"
  color="#f2f2f2"
  position="right"
  shadow="0 0 .5em rgba(0, 0, 0, 1)"
  bg-color="rgba(0, 0, 0, .75)"
  :backdrop-blur="5"
  :z-index="1000"
  :font-size="2"
  :popup-width="640"
  :popup-height="480"
  :services="services" /&gt;</code></pre>
    <h3>Adding Services</h3>
    <p>Below is a sample of a services file you can find in /node_modules/vc-social-bar/src/helpers/vc-social-bar-services.js. You can add as many as you might need.</p>
    <pre><code>/**
 * Social bar services
 * @module socialBarServices
 */

const socialBarServices = [{
  icon: 'twitter',
  url: (url) => {
    return `https://twitter.com/intent/tweet/?text=&url=${url}`
  }
}, {
  icon: 'facebook',
  url: (url) => {
    return `https://facebook.com/sharer/sharer.php?u=${url}`
  }
}, {
  icon: 'pinterest',
  url: (url) => {
    return `https://pinterest.com/pin/create/button/?url=${url}`
  }
}, {
  icon: 'linkedin',
  url: (url) => {
    return `https://www.linkedin.com/shareArticle?mini=true&url=${url}`
  }
}, {
  icon: 'xing',
  url: (url) => {
    return `https://www.xing.com/app/user?op=share;title=;url=${url}`
  }
}, {
  icon: 'reddit',
  url: (url) => {
    return `https://reddit.com/submit/?url=${url}`
  }
}, {
  icon: 'tumblr',
  url: (url) => {
    return `https://www.tumblr.com/widgets/share/tool?posttype=link&title=&caption=&shareSource=tumblr_share_button&content=${url}&canonicalUrl=${url}`
  }
}, {
  icon: 'whatsapp',
  url: (url, siteTitle) => {
    return `whatsapp://send?text=${encodeURIComponent(siteTitle)} ${url}`
  }
}, {
  icon: 'rss',
  url: () => {
    return `http://feeds.feedburner.com/acidmartin`
  }
}, {
  icon: 'email',
  url: (url, siteTitle) => {
    return `mailto:?subject=${siteTitle}&body=${url}`
  }
}]

export default socialBarServices
</code></pre>
    <p>Then you need to import to your project...</p>
    <pre><code>import services from './helpers/vc-social-bar-services'

export default {
  name: 'app',
  components: {
    VcSocialBar
  },
  data () {
    return {
      services
    }
  }
}</code></pre>
    <p>... and set it to the <code>services</code> as shown below:</p>
    <pre><code>&lt;vc-social-bar :services="services" /&gt;</code></pre>
    <h2>Props</h2>
    <ul>
      <li><code>services (Array, required)</code>: required, the social services you will use (explained above), default: <code>[]</code></li>
      <li><code>popup-width (Number, optional)</code>: width of the sharing popup in <code>px</code>, default: <code>640</code></li>
      <li><code>popup-height (Number, optional)</code>: height of the sharing popup in <code>px</code>, default: <code>548</code></li>
      <li><code>title (String, optional)</code>: sharing text, default: <code>null</code></li>
      <li><code>font-size (Number, optional)</code>: icons font size in <code>em</code>, default: <code>1</code></li>
      <li><code>color (String, optional)</code>: icons color, default: <code>"#f2f2f2"</code></li>
      <li><code>position (String, optional)</code>: bar position, one of <code>"top"</code>, <code>"right"</code>, <code>"bottom"</code>, <code>"left"</code>, default: <code>"left"</code></li>
      <li><code>bg-color (String, optional)</code>: bar background color , default: <code>"rgba(0, 0, 0, .75)"</code></li>
      <li><code>backdrop-blur (Number, optional)</code>: bar CSS <code>backdrop-filter</code> blur in <code>px</code>, default: <code>5</code></li>
      <li><code>shadow (String, optional)</code>: bar CSS <code>box-shadow</code>, default: <code>"0 0 .5em rgba(0, 0, 0, 1)"</code></li>
      <li><code>z-index (Number, optional)</code>: component z-index, default: <code>1000</code></li>
    </ul>
    <p>Version {{ version }} developed by <a href="https://wemakesites.net/" target="_blank">Martin Ivanov</a> and available on
      <a href="https://bitbucket.org/acidmartin/vc-social-bar/" target="_blank">BitBucket</a> and
      <a href="https://www.npmjs.com/package/vc-social-bar" target="_blank">NPM</a>. Check the demo <a href="https://vc-social-bar.wemakesites.net" target="_blank">here</a>.</p>
    <vc-social-bar
      title="My cool website"
      color="#f2f2f2"
      position="right"
      shadow="0 0 .5em rgba(0, 0, 0, 1)"
      bg-color="rgba(0, 0, 0, .75)"
      :backdrop-blur="5"
      :z-index="1000"
      :font-size="2"
      :popup-width="640"
      :popup-height="480"
      :services="services" />
  </div>
</template>
<script>
import VcSocialBar from './components/vc-social-bar'
import services from './helpers/vc-social-bar-services'
import { name, version, description } from '../package'

export default {
  name: 'app',
  components: {
    VcSocialBar
  },
  data () {
    return {
      name,
      version,
      description,
      services
    }
  }
}
</script>
<style>
body
{
  font-family: Arial, "Helvetica Neue", Helvetica, sans-serif;
  color: #303030;
}
</style>


// WEBPACK FOOTER //
// app.vue?e54f7b7a